import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { SidebarIcon } from "../atoms/SidebarIcon";
import { SIDEBAR_MENU_ROWS } from "../atoms/sidebarMenuRows";
import { SidebarTooltip } from "../atoms/SidebarTooltip";

interface SidebarMenuItemProps {
  /** Lucide icon component to display */
  icon: LucideIcon;
  /** Display label for the menu item */
  label: string;
  /** Whether the sidebar is collapsed (required for tooltip/label display) */
  isCollapsed: boolean;
  /** Dropdown menu items rendered inside the popover content */
  children: ReactNode;
  /** Optional className applied to DropdownMenuContent */
  contentClassName?: string;
}

/**
 * SidebarMenuItem - Molecule component
 *
 * Footer-friendly twin of SidebarNavItem: same visual treatment, but a click
 * opens a dropdown menu (Radix DropdownMenu) instead of navigating. The menu
 * pops out to the right of the sidebar so it doesn't get clipped by the
 * sidebar edge.
 */
export function SidebarMenuItem({
  icon,
  label,
  isCollapsed,
  children,
  contentClassName,
}: SidebarMenuItemProps) {
  const baseStyles = cn(
    "flex h-[1.875rem] w-full items-center gap-3 overflow-hidden rounded-md px-[0.9375rem] text-[0.75rem] font-medium transition-colors",
    "hover:bg-foreground/5 hover:text-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    // Same height rule as SidebarNavItem: rows shrink before the column
    // scrolls.
    "min-h-[1.375rem] shrink"
  );

  return (
    <DropdownMenu>
      <SidebarTooltip content={label} disabled={!isCollapsed}>
        <DropdownMenuTrigger asChild>
          <button type="button" className={baseStyles} aria-label={label}>
            <SidebarIcon icon={icon} size="md" className="shrink-0" />
            <span
              className={cn(
                "min-w-0 truncate transition-opacity duration-200",
                isCollapsed ? "opacity-0" : "opacity-100"
              )}
            >
              {label}
            </span>
          </button>
        </DropdownMenuTrigger>
      </SidebarTooltip>
      <DropdownMenuContent
        side="right"
        align="end"
        sideOffset={8}
        className={cn("w-[250px]", SIDEBAR_MENU_ROWS, contentClassName)}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
